import React, { useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CtaComponent from "../cta";
import { DateRange, DayPicker } from "react-day-picker";
import moment from "moment";
import { CTA_SIZE, CTA_TYPE, DATE_TIME_FORMATS } from "../../../data/constants";
import "./style.scss";

type PropTypes = {
  onBackBtnPress?: () => void;
  onConfirm: ({
    startDate,
    endDate,
  }: {
    startDate: moment.Moment;
    endDate: moment.Moment;
  }) => void;
  onChange?: (selectedRange: DateRange | undefined) => void;
  hideFooter?: boolean;
  rangeFromParent?: DateRange | undefined;
};

/**
 * Inline date range picker can be added to a modal, overlay, popups, in between components, forms, etc.
 * (this was not possible with antd range picker, which could)
 * this range picker is more customisable and can be modified as per the product
 * @returns
 */
function InlineDateRangePicker({
  onBackBtnPress,
  onConfirm,
  /**
   * we can let the component control range selection or
   * control the selections using onChange, rangeFromParent (may require hiding of footer using showFooter)
   * (not tested yet) - please remove this line if any one tests the parent controlled managing of the range
   */
  onChange,
  rangeFromParent,
  hideFooter = false,
}: PropTypes) {
  const [range, setRange] = useState<DateRange | undefined>();
  /**
   * taking range and display range separately as the day-picker range library only works when 'to' and 'from' is provided.
   * therefore, taking a display range to have a behaviour where first start date is selected and then end date is selected.
   */
  const [displayRange, setDisplayRange] = useState<DateRange | undefined>();

  // onPickerRangeSelect function intercepts the day-picker range selector and translates to a custom date selection logic as defined by product
  const onPickerRangeSelect = (selectedRange: DateRange | undefined) => {
    // if the range change is controlled by parent then bypass this range change logic
    if (onChange) {
      onChange(selectedRange);
      return;
    }

    /**
     * the below logic is written just for fulfilling product requirements
     * if we want default selection logic of the day-picker library, we can just use 'setRange(selectedRange)' and remove displayRange completely from this file.
     *
     * according to product requirements - first click will select 'from' and second click will select the 'to' date.
     *
     * product requirement also states that if a date is selected which is earlier than the current one,
     * then the start and end date should reset according to the newly selected one. ('to' will be empty and 'from' will be newly selected date)
     * also when a range is already selected and a date is clicked, then the 'from' should be reset to newly selected date and the next press will select the 'to' date.
     */
    if (!selectedRange && displayRange?.from && !displayRange?.to) {
      // this condition means that the same date is clicked 2 times. first start date was captured and then end date was pressed. (selectedRange is undefined when 2nd click is registered on a same date)
      setDisplayRange(range);
      return;
    }

    // if (moment(range?.to).diff(moment(range?.from), "days") > 0) { // when the previously selected range has more than one day selected (a range is already selected)
    if (displayRange?.from && displayRange.to) { // when a range is already selected
      if (!moment(range?.from).isSame(selectedRange?.from)) { // if the previously selected 'from' date is not same as the newly selected 'from' date
        setRange({
          from: selectedRange?.from,
          to: selectedRange?.from,
        });
        setDisplayRange({
          from: selectedRange?.from,
        });
      } else {
        setRange({
          from: selectedRange?.to,
          to: selectedRange?.to,
        });
        setDisplayRange({
          from: selectedRange?.to,
        });
      }
      return;
    } else if (
      moment(selectedRange?.from).isBefore(moment(range?.from)) // if the current selected start date is before previously selected start date
    ) {
      setDisplayRange({
        from: selectedRange?.from,
      });
      setRange({
        from: selectedRange?.from,
        to: selectedRange?.from,
      });
      return;
    }
    setRange(selectedRange); // select the range received from date range picker
    setDisplayRange(selectedRange);
  };

  return (
    <div className="bg-white py-16 ">
      {onBackBtnPress && (
        <button
          className="cursor-pointer z-50 relative ml-16"
          onClick={onBackBtnPress}
        >
          <ArrowBackIcon />
        </button>
      )}
      <DayPicker
        pagedNavigation
        className={`custom-daypicker px-16 ${onBackBtnPress ? "" : "pt-24"}`}
        mode="range"
        selected={rangeFromParent || range}
        onSelect={onPickerRangeSelect}
        numberOfMonths={2}
        defaultMonth={moment().toDate()}
        disabled={{ after: moment().toDate() }}
        modifiersClassNames={{
          today: 'rdp-today',
        }}
      />
      {!hideFooter && (
        <div
          style={{ boxShadow: "0 -4px 4px -1px rgba(0,0,0,0.1)" }}
          className="flex items-center px-16 justify-between pt-16 mt-16 "
        >
          <div className="flex">
            <div className="mr-30">
              <p className="opacity-60 text-[11px]">Start Date</p>
              <p className="text-[15px]">
                {!displayRange?.from
                  ? "-"
                  : moment(displayRange?.from)?.format(
                      DATE_TIME_FORMATS.dayMonthYearShort
                    )}
              </p>
            </div>
            <div>
              <p className="opacity-60 text-[11px]">End Date</p>
              <p className="text-[15px]">
                {!displayRange?.to
                  ? "-"
                  : moment(displayRange?.to)?.format(
                      DATE_TIME_FORMATS.dayMonthYearShort
                    )}
              </p>
            </div>
          </div>
          <CtaComponent
            ctaSize={CTA_SIZE.small}
            ctaType={CTA_TYPE.secondary_active_cta}
            onCtaClick={() => {
              onConfirm({
                startDate: moment(range?.from),
                endDate: moment(range?.to),
              });
            }}
            ctaText={"Confirm"}
            isDisabled={
              !moment(displayRange?.to)?.isValid() && !moment(displayRange?.from)?.isValid() || !displayRange?.to || !displayRange?.from
            }
          ></CtaComponent>
        </div>
      )}
    </div>
  );
}

export default InlineDateRangePicker;
